import { IcInfo16 } from '@assets/icons/16x16';
import { Field as ChakraField, Fieldset, Icon, Input, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import { InputNumber } from '@components/ui/input/InputNumber';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { Tooltip } from '@components/ui/tooltip';
import { DistanceUnits, unitLabel } from '@utils/unit.utils';
import { hasChanged, isInvalid } from '@utils/validation/validation.utils';
import { Field, useFormikContext } from 'formik';
import { ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PartialDrone } from '@/types/utm/drone.types';

export default function DroneStrategicTab() {
  const { formatMessage } = useIntl();
  const { errors, touched, initialValues, values, setFieldValue } = useFormikContext<PartialDrone>();

  return (
    <CockpitFormLayout title="components.utm.drones.tab.strategic.title">
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <ChakraField.Root variant="cockpit" required invalid={isInvalid('strategicDrone.name', touched, errors)}>
          <ChakraField.Label width="100%" height="20px">
            <FormattedMessage id="components.utm.drones.name" />
            <RequiredIndicator />
            {hasChanged('strategicDrone.name', initialValues, values) && <ModifiedTag />}
          </ChakraField.Label>
          <Field
            as={Input}
            type="text"
            name="strategicDrone.name"
            id="strategicDrone.name"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('strategicDrone.name', e.target.value)}
          />
        </ChakraField.Root>
        <VStack width="70%" gap={2}>
          <ChakraField.Root
            variant="cockpit"
            required
            invalid={isInvalid('strategicDrone.safetyDistance', touched, errors)}
          >
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.utm.drones.tab.strategic.satefyDistance" />
              <RequiredIndicator />
              {hasChanged('strategicDrone.safetyDistance', initialValues, values) && <ModifiedTag />}
            </ChakraField.Label>
            <Field
              as={InputNumber}
              name="strategicDrone.safetyDistance"
              id="strategicDrone.safetyDistance"
              min={0}
              suffix={unitLabel[DistanceUnits.METERS]}
              onChange={(value?: number) => {
                setFieldValue('strategicDrone.safetyDistance', value ?? null);
              }}
            />
          </ChakraField.Root>
          <ChakraField.Root
            variant="cockpit"
            required
            invalid={isInvalid('strategicDrone.criticalDistance', touched, errors)}
          >
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.utm.drones.tab.strategic.criticalDistance" />
              <Tooltip
                content={formatMessage({ id: 'components.utm.drones.validation.critical' })}
                positioning={{ placement: 'right' }}
                closeDelay={0}
              >
                <Icon as={IcInfo16} boxSize="16px" color="neutral.500" _hover={{ color: 'skyBlue.500' }} />
              </Tooltip>
              <RequiredIndicator />
              {hasChanged('strategicDrone.criticalDistance', initialValues, values) && <ModifiedTag />}
            </ChakraField.Label>
            <Field
              as={InputNumber}
              name="strategicDrone.criticalDistance"
              id="strategicDrone.criticalDistance"
              min={0}
              suffix={unitLabel[DistanceUnits.METERS]}
              onChange={(value?: number) => {
                setFieldValue('strategicDrone.criticalDistance', value ?? null);
              }}
            />
          </ChakraField.Root>
        </VStack>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
